import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import RestaurantScene from './RestaurantScene';

const HeroContainer = styled.section`
  min-height: 100vh;
  display: flex;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, var(--warm-cream) 0%, var(--soft-beige) 100%);
  justify-content: center;
  align-items: center;
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0 4rem;
  width: 45%;
  z-index: 10;
  
  @media (max-width: 768px) {
    width: 90%;
    text-align: center;
    align-items: center;
    padding: 0 1rem;
    margin-top: 2rem;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: 4rem;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  color: var(--primary-color);
  
  span {
    color: var(--secondary-color);
    display: block;
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  line-height: 1.5;
  margin-bottom: 2rem;
  max-width: 500px;
  color: var(--dark-color);
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const HeroButton = styled(motion.button)`
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 50px;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(139, 69, 19, 0.3);
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--secondary-color);
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(139, 69, 19, 0.4);
  }
`;

const HeroVisual = styled.div`
  width: 45%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 2rem;
  
  @media (max-width: 992px) {
    width: 40%;
    margin-left: 1rem;
  }
  
  @media (max-width: 768px) {
    display: flex;
    width: 90%;
    height: 300px;
    margin: 2rem 0;
    order: -1;
  }
`;

const HeroLayout = styled.div`
  display: flex;
  width: 100%;
  max-width: 1400px;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    justify-content: center;
  }
`;

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: (custom: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: custom * 0.2, duration: 0.5 }
  })
};

const FormOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
  overflow-y: auto;
`;

const FormContainer = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  position: relative;
  overflow-y: auto;
  margin: auto;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    max-width: 95%;
    max-height: 95vh;
    margin: 0.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 15px;
  }
`;

const FormTitle = styled.h2`
  color: var(--primary-color);
  text-align: center;
  margin-bottom: 1.5rem;
  font-size: 1.8rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    margin-bottom: 0.8rem;
  }
`;

const FormLabel = styled.label`
  display: block;
  color: var(--dark-color);
  font-weight: 600;
  margin-bottom: 0.3rem;
  font-size: 0.9rem;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.8rem;
  border: 2px solid #e1e1e1;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    padding: 0.7rem;
    font-size: 16px; /* Prevents zoom on iOS */
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.8rem;
  border: 2px solid #e1e1e1;
  border-radius: 8px;
  font-size: 0.9rem;
  min-height: 80px;
  resize: vertical;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    padding: 0.7rem;
    font-size: 16px; /* Prevents zoom on iOS */
    min-height: 60px;
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.8rem;
  border: 2px solid #e1e1e1;
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    padding: 0.7rem;
    font-size: 16px; /* Prevents zoom on iOS */
  }
`;

const FormButtons = styled.div`
  display: flex;
  gap: 0.8rem;
  margin-top: 1.5rem;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
    margin-top: 1rem;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.8rem;
  }
`;

const SubmitButton = styled.button`
  flex: 1;
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 0.8rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: var(--secondary-color);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 0.7rem 1rem;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  background-color: #f5f5f5;
  color: var(--dark-color);
  border: none;
  padding: 0.8rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: #e1e1e1;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 0.7rem 1rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0.8rem;
  right: 0.8rem;
  background: none;
  border: none;
  font-size: 1.3rem;
  cursor: pointer;
  color: var(--dark-color);
  width: 35px;
  height: 35px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: #f5f5f5;
  }
  
  @media (max-width: 768px) {
    top: 0.5rem;
    right: 0.5rem;
    width: 30px;
    height: 30px;
    font-size: 1.1rem;
  }
`;

const Hero: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: '',
    experience: '',
    investment: '',
    message: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Firebase Firestore'a veri ekleme
      const docRef = await addDoc(collection(db, 'reservations'), {
        ...formData,
        createdAt: Timestamp.now(),
        status: 'pending'
      });

      console.log('Rezervasyon kaydedildi, Document ID: ', docRef.id);
      alert('Rezervasyonunuz başarıyla alındı! En kısa sürede sizinle iletişime geçeceğiz.');
      
      // Formu temizle ve kapat
      setShowForm(false);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        city: '',
        experience: '',
        investment: '',
        message: ''
      });
    } catch (error) {
      console.error('Rezervasyon gönderilirken hata oluştu: ', error);
      alert('Rezervasyon gönderilirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <HeroContainer id="home">
        <HeroLayout>
          <HeroContent>
            <HeroTitle
              initial="hidden"
              animate="visible"
              custom={0}
              variants={fadeIn}
            >
              Flinstones <span>RestoCafe</span>
            </HeroTitle>
            
            <HeroSubtitle
              initial="hidden"
              animate="visible"
              custom={1}
              variants={fadeIn}
            >
              Eşsiz lezzetler ve premium kahve deneyimi ile unutulmaz anlar yaşayın. Doğal malzemeler ve özgün tariflerle hazırlanan özel menümüzü keşfedin.
            </HeroSubtitle>
            
            <HeroButton
              initial="hidden"
              animate="visible"
              custom={2}
              variants={fadeIn}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const mobileAppSection = document.getElementById('mobile-app');
                if (mobileAppSection) {
                  mobileAppSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              Rezervasyon Yap
            </HeroButton>
          </HeroContent>
          
          <HeroVisual>
            <RestaurantScene />
          </HeroVisual>
        </HeroLayout>
      </HeroContainer>

      <AnimatePresence>
        {showForm && (
          <FormOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => e.target === e.currentTarget && setShowForm(false)}
          >
            <FormContainer
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", duration: 0.5 }}
            >
              <CloseButton onClick={() => setShowForm(false)}>
                ×
              </CloseButton>
              
              <FormTitle>Rezervasyon Formu</FormTitle>
              
              <form onSubmit={handleSubmit}>
                <FormGroup>
                  <FormLabel htmlFor="firstName">Ad *</FormLabel>
                  <FormInput
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="lastName">Soyad *</FormLabel>
                  <FormInput
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="email">E-posta *</FormLabel>
                  <FormInput
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="phone">Telefon *</FormLabel>
                  <FormInput
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="city">Tarih *</FormLabel>
                  <FormInput
                    type="date"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="experience">Kişi Sayısı *</FormLabel>
                  <FormSelect
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Seçiniz...</option>
                    <option value="1">1 Kişi</option>
                    <option value="2">2 Kişi</option>
                    <option value="3">3 Kişi</option>
                    <option value="4">4 Kişi</option>
                    <option value="5">5 Kişi</option>
                    <option value="6+">6+ Kişi</option>
                  </FormSelect>
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="investment">Saat *</FormLabel>
                  <FormSelect
                    id="investment"
                    name="investment"
                    value={formData.investment}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Seçiniz...</option>
                    <option value="09:00">09:00</option>
                    <option value="10:00">10:00</option>
                    <option value="11:00">11:00</option>
                    <option value="12:00">12:00</option>
                    <option value="13:00">13:00</option>
                    <option value="14:00">14:00</option>
                    <option value="15:00">15:00</option>
                    <option value="16:00">16:00</option>
                    <option value="17:00">17:00</option>
                    <option value="18:00">18:00</option>
                    <option value="19:00">19:00</option>
                    <option value="20:00">20:00</option>
                    <option value="21:00">21:00</option>
                  </FormSelect>
                </FormGroup>

                <FormGroup>
                  <FormLabel htmlFor="message">Özel İstekler</FormLabel>
                  <FormTextarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Özel kutlama, diyet kısıtlamaları veya diğer istekleriniz..."
                    disabled={isSubmitting}
                  />
                </FormGroup>

                <FormButtons>
                  <CancelButton 
                    type="button" 
                    onClick={() => setShowForm(false)}
                    disabled={isSubmitting}
                  >
                    İptal
                  </CancelButton>
                  <SubmitButton 
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Gönderiliyor...' : 'Rezervasyon Yap'}
                  </SubmitButton>
                </FormButtons>
              </form>
            </FormContainer>
          </FormOverlay>
        )}
      </AnimatePresence>
    </>
  );
};

export default Hero; 